import { Capacitor } from '@capacitor/core';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;
let messageHandlerRegistered = false;

async function tokenDocId(token) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
  return Array.from(new Uint8Array(buf))
    .slice(0, 16)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

function getClientEmail() {
  return (localStorage.getItem('dance_booking_email') || '').toLowerCase().trim();
}

function getClientAccountId() {
  try {
    const acc = JSON.parse(localStorage.getItem('dance_booking_account'));
    return acc?.id ?? null;
  } catch {
    return null;
  }
}

// Called silently on mount and after the client sets their email, to register/refresh the token.
export async function initWebPush() {
  if (Capacitor.isNativePlatform()) return;
  if (!('Notification' in window) || !('serviceWorker' in navigator)) return;
  if (Notification.permission !== 'granted') return;
  if (!VAPID_KEY) return;

  const email = getClientEmail();
  if (!email) return;

  try {
    const messaging = getMessaging();
    const token = await getToken(messaging, { vapidKey: VAPID_KEY });
    if (!token) return;

    const id = await tokenDocId(token);
    await setDoc(doc(db, 'webPushTokens', id), {
      token,
      accountId: getClientAccountId(),
      email,
      platform: 'web',
      updatedAt: serverTimestamp(),
    }, { merge: true });

    if (!messageHandlerRegistered) {
      messageHandlerRegistered = true;
      onMessage(messaging, (payload) => {
        const { title, body, clickPath } = payload.data ?? {};
        if (!title || Notification.permission !== 'granted') return;
        const n = new Notification(title, { body, icon: '/logo192.png' });
        n.onclick = () => { window.focus(); window.location.href = clickPath ?? '/'; };
      });
    }
  } catch (err) {
    console.error('Web push init failed:', err);
  }
}

// Called from ClientSettings when the user explicitly enables notifications for the first time.
export async function requestWebPushPermission() {
  if (Capacitor.isNativePlatform()) return 'unsupported';
  if (!('Notification' in window)) return 'unsupported';
  const permission = await Notification.requestPermission();
  if (permission === 'granted') {
    await initWebPush();
  }
  return permission;
}
